import { markAuthenticated } from '@/utils/authSessionSync'
import { createRouter, createWebHistory } from 'vue-router'
import { clearOauthReturnRoute, consumeOauthReturnRoute } from '@/utils/oauthReturnRoute'
import quizzesService from '@/services/quizzes.service'
// import axios from 'axios';

const OAUTH_LINKED_PROVIDER_KEY = 'oauth_linked_provider'

const quizRouteNames = new Set([
  'constructor-steps',
  'constructor-root',
  'constructor-settings-redirect',
  'constructor-settings',
  'constructor-design',
  'admin-settings',
  'constructor-widget-site-code',
  'constructor-widget-popup',
  'constructor-widget-button',
])

const fragmentQuizExists = `
  fragment FragmentQuizExists on QuizPagination {
    data {
      id
      code
    }
  }
`

const quizExistsCache = new Map()

async function checkQuizExists(code) {
  if (!code) return false
  if (quizExistsCache.has(code)) return quizExistsCache.get(code)

  try {
    const quiz = await quizzesService.getQuizData({
      fragment: fragmentQuizExists,
      variables: { limit: 1, page: 1, code: String(code) },
    })
    const exists = Boolean(quiz?.id)
    quizExistsCache.set(code, exists)
    return exists
  } catch (error) {
    console.error('Quiz route validation failed:', error)
    const status = error?.status || error?.response?.status
    if (error?.isNetworkError || (status >= 500 && status <= 599)) {
      return null
    }
    return false
  }
}

const routes = [
  {
    path: '/',
    name: 'workboard-view',
    component: () => import('@/views/WorkboardView.vue'),
    meta: { 
        layout: 'main',
        requiresAuth: true
    }
  },
  {
    path: '/project/:project_id',
    name: 'project-view',
    component: () => import('@/views/WorkboardView.vue'),
    meta: {
      layout: 'main',
      requiresAuth: true
    }
  },
  {
    path: '/tariffs',
    name: 'tariffs-page',
    component: () => import('@/views/TariffsComparisonTable.vue'),
    meta: { 
        layout: 'user',
        requiresAuth: true
    }
  },
  {
    path: '/profile',
    name: 'profile-view',
    component: () => import('@/views/ProfileView.vue'),
    meta: { 
        layout: 'user',
        requiresAuth: true
    }
  },
  {
    path: '/team',
    name: 'team-view',
    component: () => import('@/views/TeamView.vue'),
    meta: {
      layout: 'user',
      requiresAuth: true
    }
  },
  {
    path: '/partnership',
    name: 'partnership-view',
    component: () => import('@/views/DevelopingView.vue'),
    meta: { 
        layout: 'user',
        requiresAuth: true
    }
  },
  {
    path: '/learning-center',
    name: 'learning-center-view',
    component: () => import('@/views/LearningCenterView.vue'),
    meta: {
        layout: 'user',
        requiresAuth: true
    }
  },
  {
    path: '/introduction-learning',
    name: 'introduction-learning',
    component: () => import('@/views/LearningCenterView.vue'),
    meta: {
        layout: 'user',
        requiresAuth: true,
        hideUserControl: true
    }
  },
  {
    path: '/billing',
    name: 'billing-view',
    component: () => import('@/views/BillingPage.vue'),
    meta: { 
        layout: 'user',
        requiresAuth: true
    }
  },
  {
    path: '/login',
    name: 'login-view',
    component: () => import('@/views/LoginView.vue'),
    meta: {
        layout: 'logsign',
        // guest: true,
        requiresAuth: false,
    }
  },
  {
    path: '/register',
    name: 'register-view',
    component: () => import('@/views/RegisterView.vue'),
    meta: { 
        layout: 'logsign',
        // guest: true,
        requiresAuth: false,
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPassView.vue'),
    meta: { 
        layout: 'logsign',
        // guest: true,
        requiresAuth: false,
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPassView.vue'),
    meta: { 
        layout: 'logsign',
        // guest: true,
        requiresAuth: false,
    }
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy-page',
    component: () => import('@/views/PrivacyView.vue'),
    meta: { 
        layout: 'policy',
        // guest: true,
        requiresAuth: false,
    }
  },
  {
    path: '/public-offer',
    name: 'public-offer',
    component: () => import('@/views/OfferView.vue'),
    meta: { 
        layout: 'policy',
        // guest: true,
        requiresAuth: false,
    }
  },
  {
    path: '/:code/constructor',
    name: 'constructor-steps',
    component: () => import('@/views/ConstructorSteps.vue'),
    meta: {
      layout: 'constructor',
      tab: "steps",
      requiresAuth: true,
    },
  },
  {
    path: '/:code',
    name: 'constructor-root',
    redirect: to => ({
      name: 'constructor-steps',
      params: { ...to.params }
    }),
    meta: {
      layout: 'constructor',
      tab: "steps",
      requiresAuth: true,
    }
  },
  {
    path: '/:code/settings',
    name: 'constructor-settings-redirect',
    redirect: to => ({
      name: 'constructor-settings',
      params: { ...to.params, section: 'advanced-settings' }
    }),
    meta: {
      layout: 'constructor',
      tab: "steps",
      requiresAuth: true,
    }
  },
  {
    path: '/:code/settings/:section',
    name: 'constructor-settings',
    component: () => import('@/views/ConstructorSettings.vue'),
    meta: {
      layout: 'constructor',
      tab: "steps",
      requiresAuth: true,
    }
  },
  {
    path: '/:code/design',
    name: 'constructor-design',
    component: () => import('@/views/ConstructorDesign.vue'),
    meta: {
      layout: 'constructor',
      tab: "steps",
      requiresAuth: true,
    }
  },
  {
    path: '/:code/admin-settings',
    name: 'admin-settings',
    component: () => import('@/views/SettingsAdmin.vue'),
    meta: {
      layout: 'main',
      tab: "settings",
      requiresAuth: true,
    }
  },
  {
    path: '/:code/widget-site-code',
    name: 'constructor-widget-site-code',
    component: () => import('@/views/QuizWidgetSiteCode.vue'),
    meta: {
      layout: 'main',
      tab: "settings",
      requiresAuth: true,
    }
  },
  {
    path: '/:code/widget-popup',
    name: 'constructor-widget-popup',
    component: () => import('@/views/QuizWidgetPopup.vue'),
    meta: {
      layout: 'main',
      tab: "settings",
      requiresAuth: true,
    }
  },
  {
    path: '/:code/widget-button',
    name: 'constructor-widget-button',
    component: () => import('@/views/QuizWidgetButton.vue'),
    meta: {
      layout: 'main',
      tab: "settings",
      requiresAuth: true,
    }
  },
   {
    path: '/healthz',
    name: 'health-check',
    meta: {
      layout: 'lodsign',
      requiresAuth: false,
    },
    component: {
      template: '<div>Application is healthy</div>'
    }
  },
  {
  path: '/readiness',
  name: 'readiness-check',
  meta: {
    layout: 'lodsign',
    requiresAuth: false,
  },
  component: {
    template: `<div>
                 <h2>Application is ready</h2>
                 <p>All systems are functional.</p>
               </div>`,
    mounted() {
    },
    methods: {
    }
  }
},
  {
    path: '/500',
    name: 'server-error',
    component: () => import('@/views/ServerErrorView.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
    }
  },
  {
    path: '/404',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      layout: 'public',
      requiresAuth: false,
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found-catch-all',
    redirect: { name: 'not-found' },
    meta: {
      layout: 'public',
      requiresAuth: false,
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})


function setCookie(name, value, options = {}) {

  options = {
    path: '/',
    // при необходимости добавьте другие значения по умолчанию
    ...options
  };

  if (options.expires instanceof Date) {
    options.expires = options.expires.toUTCString();
  }

  let updatedCookie = encodeURIComponent(name) + "=" + encodeURIComponent(value);

  for (let optionKey in options) {
    updatedCookie += "; " + optionKey;
    let optionValue = options[optionKey];
    if (optionValue !== true) {
      updatedCookie += "=" + optionValue;
    }
  }

  document.cookie = updatedCookie;
}
function deleteCookie(name) {
  setCookie(name, "", {
    'max-age': -1
  })
}

router.beforeEach(async (to, from, next) => {
  let isLogin = localStorage.getItem('isLogin')
  const { provider, oauth_result: oauthResult } = to.query || {}
  const oauthSuccessResults = new Set(['login', 'registered', 'linked'])
  const hasOauthCallback = provider && oauthResult
  const isOauthSuccess = provider && oauthSuccessResults.has(oauthResult)

  if (isOauthSuccess) {
    markAuthenticated()
    isLogin = '1'
    if (oauthResult === 'linked' && provider && typeof window !== 'undefined') {
      window.sessionStorage.setItem(OAUTH_LINKED_PROVIDER_KEY, String(provider))
    }
    const oauthReturnRoute = consumeOauthReturnRoute()
    if (oauthReturnRoute && oauthReturnRoute !== to.fullPath) {
      next({ path: oauthReturnRoute, replace: true })
      return
    }
    if (oauthResult === 'linked' && (to.name !== 'workboard-view' || Object.keys(to.query || {}).length > 0)) {
      next({ name: 'workboard-view', replace: true })
      return
    }
  } else if (hasOauthCallback) {
    clearOauthReturnRoute()
  }
  if (to.path == '/POST') {
    next({
        path: '/',
    })
    return
  }

  const shouldValidateQuizRoute = quizRouteNames.has(to.name) && to.params?.code

  if (shouldValidateQuizRoute && isLogin == '1') {
    const exists = await checkQuizExists(to.params.code)

    if (exists === null) {
      next({
        name: 'server-error',
        query: { from: to.fullPath },
        replace: true,
      })
      return
    }

    if (!exists) {
      next({
        name: 'not-found',
        query: { from: to.fullPath },
        replace: true,
      })
      return
    }
  }
  if (to.path == '/login') {
    if (isLogin == '0') {
      next()
      return
    } else if (isLogin == '1') {
      next({ name: 'workboard-view'})
      return
    }
    
  }
  if(to.matched.some(record => record.meta.requiresAuth)) {
    if (isLogin == '0') {
        deleteCookie('adsquiz_session')
        deleteCookie('XSRF-TOKEN')
        next({
            name: 'login-view',
            params: { nextUrl: to.fullPath }
        })
    } else {
      next()
    }
  } else if(to.matched.some(record => record.meta.guest)) {
      if(isLogin == false){
          next()
      }
      else{
          next({ name: 'workboard-view'})
      }
  }else {
      next() 
  }
})

export default router
